import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { X, Check, Clock, Printer } from "lucide-react";

export default function ComplianceReport({ meetingId, title, onClose }) {
    const location = useLocation();
    const rolePrefix = location.pathname.startsWith("/foreman") ? "/foreman" : "/admin";
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get(`/safety/meetings/${meetingId}/acks`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
    }, [meetingId]);

    const pct = data && data.total ? Math.round((data.watched_count / data.total) * 100) : 0;

    return (
        <div className="fixed inset-0 z-50 bg-brand-charcoal/80 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="compliance-report">
            <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl shadow-lift max-h-[92vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-brand-admin-header text-brand-bone shrink-0">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-brand-gold">Watch report</div>
                        <div className="font-bold truncate" data-testid="report-title">{title}</div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/15 shrink-0" data-testid="report-close"><X className="h-5 w-5" /></button>
                </div>

                {!data ? (
                    <div className="p-8 text-center text-brand-charcoal/60">Loading…</div>
                ) : data.error ? (
                    <div className="p-8 text-center text-brand-danger">Could not load report.</div>
                ) : (
                    <div className="overflow-auto p-5 space-y-4">
                        <div className="bg-brand-stone rounded-xl p-4" data-testid="report-summary">
                            <div className="flex items-end justify-between">
                                <span className="text-sm font-bold text-brand-charcoal">Watched</span>
                                <span className="font-bold text-2xl text-brand-success">{data.watched_count}<span className="text-brand-charcoal/50 text-base"> / {data.total}</span></span>
                            </div>
                            <div className="mt-2 h-2.5 rounded-full bg-brand-charcoal/10 overflow-hidden">
                                <div className="h-full bg-brand-success transition-all" style={{ width: `${pct}%` }} />
                            </div>
                        </div>

                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-brand-success mb-2 flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Watched ({data.watched.length})</div>
                            <ul className="space-y-1" data-testid="report-watched-list">
                                {data.watched.length === 0 && <li className="text-sm text-brand-charcoal/50">No one yet.</li>}
                                {data.watched.map((w) => (
                                    <li key={w.employee_id} className="flex justify-between text-sm bg-brand-success/10 rounded-lg px-3 py-2">
                                        <span className="font-medium text-brand-charcoal">{w.name}</span>
                                        <span className="text-brand-charcoal/50">{dayjs(w.watched_at).format("MMM D, h:mm A")}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-brand-danger mb-2 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Not watched yet ({data.not_watched.length})</div>
                            <ul className="space-y-1" data-testid="report-notwatched-list">
                                {data.not_watched.length === 0 && <li className="text-sm text-brand-success font-semibold">Everyone is covered. 🦅</li>}
                                {data.not_watched.map((w) => (
                                    <li key={w.employee_id} className="text-sm bg-brand-danger/10 rounded-lg px-3 py-2 font-medium text-brand-charcoal">{w.name}</li>
                                ))}
                            </ul>
                        </div>

                        <a
                            href={`${rolePrefix}/safety/${meetingId}/report`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-center gap-1.5 h-11 rounded-xl bg-brand-stone hover:bg-brand-gold/40 text-brand-charcoal font-bold text-sm"
                            data-testid="open-printable-report"
                        >
                            <Printer className="h-4 w-4" /> Open full printable report
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}
